import '../../styles/fonts.css';
import React, { useEffect, useRef } from 'react';
import { Message } from '../../types/chat';
import { formatTime } from '../../utils/formatters';
import { EmojiRenderer } from '../common/EmojiRenderer';
import Thonderball from '../../assets/thunderballSimple.png';
import Group from '../../assets/group.png';

interface MessageListProps {
  messages: Message[];
  currentUserNumber: string;
  companionNumber: string;
  isGroupChat?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserNumber,
  companionNumber,
  isGroupChat,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getMessageDate = (message: Message) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let messageDate = new Date(message.createdAt);
    messageDate.setHours(0, 0, 0, 0);

    const isToday = messageDate.toDateString() === today.toDateString();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = messageDate.toDateString() === yesterday.toDateString();

    if (isToday) return 'Сегодня';
    if (isYesterday) return 'Вчера';
    return messageDate.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });
  };

  let lastDate: string | null = null;

  if (messages.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '32px',
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#666',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <div style={{ height: '54px', width: '54px' }}>
          <img
            src={isGroupChat ? Group : Thonderball}
            alt='Mario'
            style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = '🍄';
            }}
          />
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '0px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {isGroupChat ? <>{companionNumber}</> : <>Ты начал чат с {companionNumber}</>}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {messages.map((msg, index) => {
        const messageDate = getMessageDate(msg);
        const showDateSeparator = messageDate !== lastDate;
        if (showDateSeparator) {
          lastDate = messageDate;
        }

        return (
          <React.Fragment key={`${msg.id}-${msg.tempId || ''}-${index}`}>
            {showDateSeparator && (
              <div
                style={{
                  textAlign: 'center',
                  margin: '16px 0 8px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#666',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    backgroundColor: '#e8e8e8',
                    padding: '0 12px',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {messageDate}
                </span>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#ccc',
                    zIndex: 0,
                  }}
                />
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '6px',
                width: '100%',
                opacity: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  flexDirection: 'column',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 'fit-content',
                    color: '#666',
                  }}
                >
                  [{msg.timestamp || formatTime(msg.createdAt)}]
                </div>
                <div
                  style={{
                    width: 'fit-content',
                    color: '#666',
                  }}
                >
                  [{msg.senderNumber === currentUserNumber ? 'ты' : msg.senderNumber}]
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: '#000',
                    marginTop: '4px',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    lineHeight: '1.4',
                  }}
                >
                  <EmojiRenderer text={msg.content} />
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};
